import { draftText, DRAFT_LINE_LABEL, numberWarnings, shortDay, withTitles, type DraftLine, type SendDraft } from "@boem/core/bridge";
import { AREA_TAGS, fmtDate, flowLane, KINDS, projectCode, type Board, type BoardBridge, type BoardItem, type ItemKind, type Lane } from "@boem/core/shared";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Avatar, Linkify } from "../util.tsx";
import { ApiError, call } from "../api.ts";
import { apiBase } from "../org.ts";
import { Icon } from "./Icons.tsx";
import { ErrorLine, Modal, SecretHint, useSecretCheck } from "./Modal.tsx";

// Links from a personal board to OEMUAP on screen (docs/design/orgs.md 6): the On OEMUAP block on a
// linked personal card, Link to an OEMUAP item, Create an OEMUAP item from this, and Copy to my board
// from an OEMUAP card. A company item never opens inside a personal board: ↗ opens it on OEMUAP in a
// new tab. Nothing personal goes to OEMUAP unless the person writes it into a dialog and saves.

export const bridgeApi = {
  link: (num: number, to: string | number) => call<{ num: number; label: string }>("POST", `${apiBase()}/items/${num}/bridge`, { to }),
  unlink: (num: number) => call("DELETE", `${apiBase()}/items/${num}/bridge`),
  createCompany: (num: number, draft: CompanyDraftInput) => call<{ num: number; label: string }>("POST", `${apiBase()}/items/${num}/bridge/create`, draft),
  copyFromCompany: (num: number, opts: { notes: boolean; shots: boolean; link: boolean; parent: number | null; edit: { title: string; body: string; kind: ItemKind | null; priority: string } }) =>
    call<{ num: number; slug: string }>("POST", `/api/items/${num}/copy-to-mine`, opts),
  /** OEMUAP's board, for the search and its projects (the person reads OEMUAP like everyone on the team) */
  company: () => call<Board>("GET", "/api/board"),
  /** Send updates: what's waiting, then one note on the OEMUAP item */
  draft: (num: number) => call<SendDraft>("GET", `${apiBase()}/items/${num}/bridge/draft`),
  send: (num: number, body: { text: string; eventIds: string[]; skipped: string[]; bridgeId: string; sendKey: string }) =>
    call<{ num: number; label: string; noteId: string; already: boolean }>("POST", `${apiBase()}/items/${num}/bridge/send`, body),
};

export interface CompanyDraftInput {
  title: string;
  body: string;
  kind: ItemKind | null;
  priority: string;
  tags: string[];
  parent: number | null;
  note: string | null;
  shots: boolean;
}

const LANE_LABEL: Record<Lane, string> = { todo: "To do", review: "PR review", deploy: "Developed", verify: "Deployed", closed: "Closed" };

/** "02-03" or "#103": how OEMUAP names one of its items. */
const companyRef = (it: Pick<BoardItem, "num" | "key" | "kind" | "projectNo">) => it.key?.label ?? (it.kind === "project" && it.projectNo != null ? projectCode(it.projectNo) : `#${it.num}`);

/** The OEMUAP item's stage, as its own card's pill says it. */
function StagePill({ t }: { t: NonNullable<BoardBridge["target"]> }) {
  const lane = flowLane(t);
  return <span className={`pill s-${t.status} l-${lane}`}>{t.kind === "task" && lane !== "closed" ? "To do" : lane === "closed" && t.stage === "verified" ? "Verified" : LANE_LABEL[lane]}</span>;
}

/**
 * On OEMUAP: the linked company item read live (its number, title, stage and who has it), when
 * updates were last sent, and ↗ to open it on OEMUAP in a new tab.
 */
export function OnOemuap({ b, onSend }: { b: BoardBridge; onSend?: () => void }) {
  const t = b.target;
  return (
    <div className="xorg" aria-label={`Linked to OEMUAP ${b.label}`}>
      <div className="xorg-h">
        <span className="fl">{b.kind === "cloned_from" ? "Copied from" : "Linked to"}</span>
        <span className="org-tag co">OEMUAP</span>
        <span className="xorg-sent">{b.lastSent ? `last sent ${fmtDate(b.lastSent)}` : "nothing sent yet"}</span>
      </div>
      <div className="xorg-b">
        <span className="xorg-no">
          <b>{b.label}</b>
          {b.label !== `#${b.num}` && b.num > 0 && <span className="sub">#{b.num}</span>}
        </span>
        <span className="xorg-t">{t ? t.title : "You can't open this OEMUAP item now."}</span>
        {t && <StagePill t={t} />}
        {t?.assignee && <span className="xorg-who">{t.assignee}</span>}
        {onSend && t && (
          <button type="button" className="btn primary xorg-send" onClick={onSend} title={b.waiting ? `${b.waiting} update${b.waiting === 1 ? "" : "s"} waiting to go to ${b.label}` : `Nothing new since the last send`}>
            Send updates{b.waiting ? ` · ${b.waiting}` : ""}
          </button>
        )}
        <a className="xorg-open" href={b.href} target="_blank" rel="noopener" title="Open it on OEMUAP, in a new tab" aria-label={`Open ${b.label} on OEMUAP in a new tab`}>
          <Icon name="goto" />
        </a>
      </div>
    </div>
  );
}

/** The eyebrow of a cross-board dialog: GAUTAM'S BOARD 01-03 → OEMUAP 02-03. */
function Eyebrow({ from, to, mine }: { from: string; to: string; mine: "from" | "to" }) {
  const pe = (
    <span className="org-tag pe">
      <Icon name="lock" /> Your board
    </span>
  );
  const co = <span className="org-tag co">OEMUAP</span>;
  return (
    <div className="dlg-eyebrow">
      {mine === "from" ? pe : co} {from} <span aria-hidden="true">&rarr;</span> {mine === "from" ? co : pe} {to}
    </div>
  );
}

/** OEMUAP's board, loaded once for a dialog: its items for the search and its projects. */
function useCompanyBoard() {
  const [board, setBoard] = useState<Board | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    bridgeApi.company().then(setBoard, (e) => setError(e instanceof ApiError ? e.message : "Couldn't load OEMUAP's items."));
  }, []);
  return { board, error };
}

/** Link a personal item to an OEMUAP item: search OEMUAP's open and recent items, pick one, Link. */
export function LinkModal({ item, onClose, onDone }: { item: BoardItem; onClose: () => void; onDone: (r: { num: number; label: string }) => void }) {
  const { board, error: loadError } = useCompanyBoard();
  const [q, setQ] = useState("");
  const [pick, setPick] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const ref = item.key?.label ?? `#${item.num}`;
  const hits = useMemo(() => {
    if (!board) return [];
    const s = q.trim().toLowerCase();
    const open = board.items.filter((i) => i.kind !== "project");
    const match = (i: BoardItem) => !s || i.title.toLowerCase().includes(s) || `#${i.num}` === s || String(i.num) === s.replace("#", "") || (i.key?.label ?? "") === s;
    return open
      .filter(match)
      .sort((a, b) => Number(a.status === "closed") - Number(b.status === "closed") || (a.key?.label ?? "~").localeCompare(b.key?.label ?? "~") || b.num - a.num)
      .slice(0, 40);
  }, [board, q]);
  const go = async () => {
    if (pick == null) return;
    setBusy(true);
    setError(null);
    try {
      onDone(await bridgeApi.link(item.num, pick));
      onClose();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Couldn't reach the server. Try again.");
    } finally {
      setBusy(false);
    }
  };
  return (
    <Modal onClose={onClose} wide>
      <div className="org-dlg">
        <Eyebrow from={ref} to="" mine="from" />
        <h3>Link {ref} to an OEMUAP item</h3>
        <p className="ppl-lead">{item.title}</p>
        <div className="fl">Find it on OEMUAP</div>
        <input className="search" type="search" value={q} placeholder="Title, #103 or 02-03" aria-label="Find it on OEMUAP" onChange={(e) => setQ(e.target.value)} />
        <div className="xorg-pick" role="radiogroup" aria-label="OEMUAP items">
          {!board && !loadError && <div className="pk-none">Loading OEMUAP&rsquo;s items&hellip;</div>}
          {board && !hits.length && <div className="pk-none">Nothing on OEMUAP matches.</div>}
          {hits.map((i) => (
            <label key={i.num} className="xorg-hit">
              <input type="radio" name="xorg-pick" checked={pick === i.num} onChange={() => setPick(i.num)} />
              <b>{companyRef(i)}</b>
              <span className="t">{i.title}</span>
              <span className={`pill s-${i.status} l-${flowLane(i)}`}>{LANE_LABEL[flowLane(i)]}</span>
            </label>
          ))}
        </div>
        <p className="xorg-note">Only your board shows the link. OEMUAP&rsquo;s item shows nothing until you send it updates, and then only the note you post.</p>
        <ErrorLine error={error ?? loadError} />
        <div className="row">
          <span className="sp" />
          <button type="button" className="btn" onClick={onClose}>
            Cancel
          </button>
          <button type="button" className="btn primary" disabled={busy || pick == null} onClick={go}>
            Link
          </button>
        </div>
      </div>
    </Modal>
  );
}

/** "From my notes:\n- Oct 4: ..." : the personal item's notes, collated for its first OEMUAP note (each editable after). */
export function collatedNotes(item: BoardItem, me: string): string {
  if (!item.notes.length) return "";
  return ["From my notes:", ...item.notes.map((n) => `- ${fmtDate(n.at).replace(/, \d{4}$/, "")}: ${n.name === me ? "" : `${n.name}: `}${n.body}`)].join("\n");
}

/** Kind and priority as chips, as on the mockup's dialogs. */
function Chips<T extends string | null>({ label, value, options, onChange }: { label: string; value: T; options: { id: T; label: string }[]; onChange: (v: T) => void }) {
  return (
    <div>
      <div className="fl">{label}</div>
      <div className="asg-chips" role="radiogroup" aria-label={label}>
        {options.map((o) => (
          <button key={String(o.id)} type="button" className="chip" role="radio" aria-checked={value === o.id} aria-pressed={value === o.id} onClick={() => onChange(o.id)}>
            {o.label}
          </button>
        ))}
      </div>
    </div>
  );
}

const ITEM_KINDS = KINDS.filter((k) => k.id !== "project").map((k) => ({ id: k.id as ItemKind | null, label: k.label }));
const PRIORITIES = [
  { id: "high", label: "High" },
  { id: "normal", label: "Normal" },
  { id: "low", label: "Low" },
];

/**
 * Create an OEMUAP item from this: prefilled from the personal item, everything editable, checked for
 * credentials as you type (and again on the server). Its notes, collated, can be its first note.
 */
export function CreateCompanyModal({ item, me, onClose, onDone }: { item: BoardItem; me: string; onClose: () => void; onDone: (r: { num: number; label: string }) => void }) {
  const { board, error: loadError } = useCompanyBoard();
  const [title, setTitle] = useState(item.title);
  const [body, setBody] = useState(item.body);
  const [kind, setKind] = useState<ItemKind | null>(item.kind === "project" ? null : item.kind);
  const [priority, setPriority] = useState<string>(item.priority);
  const [tags, setTags] = useState<string[]>(item.tags.filter((t) => AREA_TAGS.some((a) => a.id === t)));
  const [parent, setParent] = useState<number | null>(null);
  const [withNotes, setWithNotes] = useState(item.notes.length > 0);
  const [note, setNote] = useState(() => collatedNotes(item, me));
  const cleared = item.shots.filter((s) => s.scan === "clean" || s.scan === "unscanned").length;
  const [shots, setShots] = useState(cleared > 0);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const secrets = useSecretCheck(title, body, withNotes ? note : "");
  const projects = (board?.items ?? []).filter((i) => i.kind === "project" && i.status === "open").sort((a, b) => (a.projectNo ?? 999) - (b.projectNo ?? 999));
  const ref = item.key?.label ?? `#${item.num}`;
  const toggle = (t: string) => setTags((cur) => (cur.includes(t) ? cur.filter((x) => x !== t) : [...cur, t]));
  const go = async () => {
    setBusy(true);
    setError(null);
    try {
      onDone(await bridgeApi.createCompany(item.num, { title: title.trim(), body: body.trim(), kind, priority, tags, parent, note: withNotes && note.trim() ? note.trim() : null, shots }));
      onClose();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Couldn't reach the server. Try again.");
    } finally {
      setBusy(false);
    }
  };
  return (
    <Modal onClose={onClose} wide>
      <div className="org-dlg">
        <Eyebrow from={ref} to="a new item" mine="from" />
        <h3>Create an OEMUAP item from {ref}</h3>
        <p className="ppl-lead">Prefilled from your item. Change anything before it goes on OEMUAP, where everyone on the team will see it.</p>
        <div className="fl">Title</div>
        <input className="search" type="text" value={title} maxLength={200} aria-label="Title" onChange={(e) => setTitle(e.target.value)} />
        <div className="fl">Description</div>
        <textarea rows={4} value={body} aria-label="Description" onChange={(e) => setBody(e.target.value)} />
        <div className="form-grid">
          <Chips label="Kind" value={kind} options={ITEM_KINDS} onChange={(k) => setKind(kind === k ? null : k)} />
          <Chips label="Priority" value={priority} options={PRIORITIES} onChange={setPriority} />
        </div>
        <div className="form-grid">
          <div>
            <div className="fl">Where</div>
            <div className="asg-chips">
              {AREA_TAGS.map((t) => (
                <button key={t.id} type="button" className="chip" aria-pressed={tags.includes(t.id)} onClick={() => toggle(t.id)}>
                  {t.label}
                </button>
              ))}
            </div>
          </div>
          <div>
            <div className="fl">Project on OEMUAP</div>
            <select className="search" aria-label="Project on OEMUAP" value={parent ?? ""} onChange={(e) => setParent(e.target.value ? Number(e.target.value) : null)}>
              <option value="">No project</option>
              {projects.map((p) => (
                <option key={p.num} value={p.num}>
                  {p.projectNo != null ? projectCode(p.projectNo) : `#${p.num}`} {p.title}
                </option>
              ))}
            </select>
          </div>
        </div>
        <label className="tm-check">
          <input type="checkbox" checked={withNotes} disabled={!item.notes.length} onChange={(e) => setWithNotes(e.target.checked)} /> Add my notes as its first note
          <span className="h">
            ({item.notes.length}, collated; edit them here)
          </span>
        </label>
        {withNotes && <textarea rows={4} value={note} aria-label="Its first note" onChange={(e) => setNote(e.target.value)} />}
        <label className="tm-check">
          <input type="checkbox" checked={shots} disabled={!cleared} onChange={(e) => setShots(e.target.checked)} /> Copy the screenshots
          <span className="h"> ({cleared}, cleared by the scan)</span>
        </label>
        <SecretHint findings={secrets} />
        <p className="xorg-note">
          It goes on OEMUAP with the next number there, logged by you and marked <b>From your personal board</b>. Your {ref} stays here, linked to it, so you can send it updates.
          OEMUAP never sees {ref} or your board&rsquo;s numbers.
        </p>
        <ErrorLine error={error ?? loadError} />
        <div className="row">
          <span className="sp" />
          <button type="button" className="btn" onClick={onClose}>
            Cancel
          </button>
          <button type="button" className="btn primary" disabled={busy || title.trim().length < 3 || secrets.length > 0} onClick={go}>
            Create on OEMUAP
          </button>
        </div>
      </div>
    </Modal>
  );
}

/**
 * Copy to my board, from an OEMUAP card: a private copy on my board, prefilled and editable, linked to
 * the OEMUAP item unless I say not to. Nothing changes on OEMUAP.
 */
export function CopyFromCompanyModal({ item, mySlug, onClose, onDone }: { item: BoardItem; mySlug: string; onClose: () => void; onDone: (r: { num: number; slug: string }) => void }) {
  const [title, setTitle] = useState(item.title);
  const [body, setBody] = useState(item.body);
  const [kind, setKind] = useState<ItemKind | null>(item.kind === "project" ? null : item.kind);
  const [priority, setPriority] = useState<string>(item.priority);
  const [notes, setNotes] = useState(item.notes.length > 0);
  const cleared = item.shots.filter((s) => s.scan === "clean" || s.scan === "unscanned").length;
  const [shots, setShots] = useState(cleared > 0);
  const [link, setLink] = useState(true);
  const [projects, setProjects] = useState<BoardItem[] | null>(null);
  const [parent, setParent] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const secrets = useSecretCheck(title, body);
  useEffect(() => {
    call<Board>("GET", `/api/orgs/${encodeURIComponent(mySlug)}/board`).then(
      (b) => setProjects(b.items.filter((i) => i.kind === "project" && i.status === "open")),
      () => setProjects([]),
    );
  }, [mySlug]);
  const ref = companyRef(item);
  const go = async () => {
    setBusy(true);
    setError(null);
    try {
      onDone(await bridgeApi.copyFromCompany(item.num, { notes, shots, link, parent, edit: { title: title.trim(), body: body.trim(), kind, priority } }));
      onClose();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Couldn't reach the server. Try again.");
    } finally {
      setBusy(false);
    }
  };
  return (
    <Modal onClose={onClose} wide>
      <div className="org-dlg">
        <Eyebrow from={ref} to="" mine="to" />
        <h3>Copy {ref} to my board</h3>
        <p className="ppl-lead">A private copy on your board, prefilled from OEMUAP. Edit it first if you like.</p>
        <div className="fl">Title</div>
        <input className="search" type="text" value={title} maxLength={200} aria-label="Title" onChange={(e) => setTitle(e.target.value)} />
        <div className="fl">Description</div>
        <textarea rows={4} value={body} aria-label="Description" onChange={(e) => setBody(e.target.value)} />
        <div className="form-grid">
          <Chips label="Kind" value={kind} options={ITEM_KINDS} onChange={(k) => setKind(kind === k ? null : k)} />
          <Chips label="Priority" value={priority} options={PRIORITIES} onChange={setPriority} />
        </div>
        <div className="fl">Put it in (your board)</div>
        <select className="search" aria-label="Put it in" value={parent ?? ""} onChange={(e) => setParent(e.target.value ? Number(e.target.value) : null)}>
          <option value="">No project</option>
          {(projects ?? []).map((p) => (
            <option key={p.num} value={p.num}>
              {p.projectNo != null ? projectCode(p.projectNo) : `#${p.num}`} {p.title}
            </option>
          ))}
        </select>
        <label className="tm-check">
          <input type="checkbox" checked={notes} disabled={!item.notes.length} onChange={(e) => setNotes(e.target.checked)} /> Copy its notes as one note
          <span className="h"> ({item.notes.length}, with who wrote each and when)</span>
        </label>
        {cleared > 0 && (
          <label className="tm-check">
            <input type="checkbox" checked={shots} onChange={(e) => setShots(e.target.checked)} /> Copy screenshots
            <span className="h"> ({cleared}, cleared by the scan)</span>
          </label>
        )}
        <label className="tm-check">
          <input type="checkbox" checked={link} onChange={(e) => setLink(e.target.checked)} /> Link it to {ref}, so I can send it updates
        </label>
        <SecretHint findings={secrets} />
        <p className="xorg-note">It gets your board&rsquo;s next number. Nothing changes on OEMUAP: {ref}&rsquo;s history and the managers&rsquo; log don&rsquo;t mention this, and nobody there sees your copy.</p>
        <ErrorLine error={error} />
        <div className="row">
          <span className="sp" />
          <button type="button" className="btn" onClick={onClose}>
            Cancel
          </button>
          <button type="button" className="btn primary" disabled={busy || title.trim().length < 3 || secrets.length > 0} onClick={go}>
            Copy to my board
          </button>
        </div>
      </div>
    </Modal>
  );
}

/**
 * Send updates (orgs.md 6.3): one tick per line waiting since the last send, the note OEMUAP gets
 * (built from the ticked lines, then edited freely), a warning for numbers that are this board's, and
 * a preview of the note as the OEMUAP card will show it. Unticked lines are left out for good.
 */
export function SendUpdatesModal({ item, me, onClose, onDone }: { item: BoardItem; me: string; onClose: () => void; onDone: (r: { label: string; num: number; already: boolean }) => void }) {
  const [draft, setDraft] = useState<SendDraft | null>(null);
  const [ticked, setTicked] = useState<Set<string>>(new Set());
  const [text, setText] = useState("");
  const [edited, setEdited] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [stale, setStale] = useState(false);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => {
    setError(null);
    setStale(false);
    bridgeApi.draft(item.num).then(
      (d) => {
        setDraft(d);
        setTicked(new Set(d.lines.map((l) => l.eventId)));
        setText(draftText(d.lines));
        setEdited(false);
      },
      (e) => setError(e instanceof ApiError ? e.message : "Couldn't reach the server. Try again."),
    );
  }, [item.num]);
  useEffect(load, [load]);
  const secrets = useSecretCheck(text);
  const warnings = useMemo(() => (draft ? numberWarnings(text, draft.items) : []), [draft, text]);
  const ref = item.key?.label ?? `#${item.num}`;
  const to = draft?.label ?? item.bridge?.label ?? "OEMUAP";
  const toggle = (l: DraftLine) => {
    if (!draft) return;
    const next = new Set(ticked);
    if (next.has(l.eventId)) next.delete(l.eventId);
    else next.add(l.eventId);
    // once edited by hand, ticking a line asks before rebuilding the text
    if (edited && !window.confirm("Rebuild the note from the ticked lines? Your edits to it are lost.")) {
      setTicked(next);
      return;
    }
    setTicked(next);
    setText(draftText(draft.lines.filter((x) => next.has(x.eventId))));
    setEdited(false);
  };
  const post = async () => {
    if (!draft) return;
    setBusy(true);
    setError(null);
    try {
      const eventIds = draft.lines.filter((l) => ticked.has(l.eventId)).map((l) => l.eventId);
      const skipped = draft.lines.filter((l) => !ticked.has(l.eventId)).map((l) => l.eventId);
      const r = await bridgeApi.send(item.num, { text: text.trim(), eventIds, skipped, bridgeId: draft.bridgeId, sendKey: draft.sendKey });
      onDone(r);
      onClose();
    } catch (e) {
      const conflict = e instanceof ApiError && e.status === 409;
      setStale(conflict && /review it again/.test(e.message));
      setError(e instanceof ApiError ? e.message : "Couldn't reach the server. Try again.");
    } finally {
      setBusy(false);
    }
  };
  const since = draft?.lastSent ? `Since your last send, ${fmtDate(draft.lastSent)}` : "Since you linked it";
  return (
    <Modal onClose={onClose} wide>
      <div className="org-dlg send-dlg">
        <Eyebrow from={ref} to={to} mine="from" />
        <h3>
          Send updates to <span className="nowrap">OEMUAP {to}</span>
          {draft && to !== `#${draft.num}` ? ` · #${draft.num}` : ""}
        </h3>
        <p className="ppl-lead">
          {since}. One note goes on {to} as you, marked <b>From your personal board</b>. Nothing else from your board goes with it.
        </p>
        {!draft && !error && <p className="ppl-note">Collecting what&rsquo;s new&hellip;</p>}
        {draft && (
          <div className="send-cols">
            <div>
              <div className="fl">
                New {draft.lastSent ? `since ${shortDay(draft.lastSent)}` : "since you linked it"} · {draft.lines.length}
              </div>
              {!draft.lines.length && <p className="ppl-note">Nothing new since the last send.</p>}
              <ul className="send-lines">
                {draft.lines.map((l) => (
                  <li key={l.eventId}>
                    <label className="tm-check send-line">
                      <input type="checkbox" checked={ticked.has(l.eventId)} onChange={() => toggle(l)} />
                      <span>
                        <span className="send-when">
                          {shortDay(l.at)} · {DRAFT_LINE_LABEL[l.kind]}
                        </span>
                        <span className="send-text">{l.text}</span>
                      </span>
                    </label>
                  </li>
                ))}
              </ul>
              <p className="ppl-note">
                Not sent: {draft.notSent.shots ? `${draft.notSent.shots} screenshot${draft.notSent.shots === 1 ? "" : "s"} (add ${draft.notSent.shots === 1 ? "it" : "them"} on ${to} yourself if it helps), and ` : ""}
                moves, assignments and pins on your board. A line you untick is left out and doesn&rsquo;t come back next time.
              </p>
            </div>
            <div>
              <div className="fl">The note OEMUAP gets</div>
              {warnings.map((w) => (
                <div key={w.ref} className="send-warn" role="alert">
                  <span>{w.message}</span>
                  <button type="button" className="btn" onClick={() => setText(withTitles(text, [w]))}>
                    Use the title instead
                  </button>
                </div>
              ))}
              <textarea
                rows={8}
                value={text}
                aria-label="The note OEMUAP gets"
                onChange={(e) => {
                  setText(e.target.value);
                  setEdited(true);
                }}
              />
              <SecretHint findings={secrets} />
              <div className="fl">How {to} shows it</div>
              <div className="send-preview">
                <div className="send-pv-h">
                  <Avatar name={me} /> <b>{me}</b> <span className="send-when">now</span>
                </div>
                <div className="send-pv-b">{text.trim() ? <Linkify text={text.trim()} /> : <i>(empty)</i>}</div>
                <div className="origin from-pb compact">
                  <span className="src">Personal board</span>
                  <span>
                    From <b>{me}</b>&rsquo;s personal board · sent just now
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}
        <ErrorLine error={error} />
        <div className="row">
          {stale && (
            <button type="button" className="btn" onClick={load}>
              Review again
            </button>
          )}
          <span className="sp" />
          <button type="button" className="btn" onClick={onClose}>
            Cancel
          </button>
          <button type="button" className="btn primary" disabled={busy || !draft || !draft.lines.length || !text.trim() || secrets.length > 0} onClick={post}>
            Post to {to}
          </button>
        </div>
      </div>
    </Modal>
  );
}
